import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  DisciplineInput,
  DisciplineOutput,
  DisciplineOutputPagination,
  DisciplineQueryInput,
  MutationCreateDisciplineArgs,
  MutationRemoveDisciplineArgs,
  MutationUpdateDisciplineArgs,
  UpdateDisciplineInput,
} from '../gql/graphql';
import { graphqlFetch } from '@/utils/graphqlFetch';
import { GET_DISCIPLINES, GET_DISCIPLINES_PAGINATED } from '../queries';
import {
  CREATE_DISCIPLINE,
  REMOVE_DISCIPLINE,
  UPDATE_DISCIPLINE,
} from '../mutations';

export const useGetDiscplines = (queries?: DisciplineOutput) => {
  return useQuery<DisciplineOutput[], Error>({
    queryKey: ['disciplines', queries],
    queryFn: async () => {
      const data = await graphqlFetch<
        { getDisciplines: DisciplineOutput[] },
        null
      >(GET_DISCIPLINES);
      return data.getDisciplines;
    },
  });
};

export const useGetDisciplinesPaginated = (query: DisciplineQueryInput) => {
  return useQuery<DisciplineOutputPagination, Error>({
    // Sous ['disciplines'] pour être couverte par les invalidations des mutations
    queryKey: ['disciplines', 'paginated', query],
    queryFn: async () => {
      const data = await graphqlFetch<
        { getDisciplinesWithPagination: DisciplineOutputPagination },
        { query: DisciplineQueryInput }
      >(GET_DISCIPLINES_PAGINATED, { query });
      return data.getDisciplinesWithPagination;
    },
  });
};

export const useCreateDiscipline = () => {
  const queryClient = useQueryClient();
  return useMutation<DisciplineOutput, Error, DisciplineInput>({
    mutationFn: async (input) => {
      const data = await graphqlFetch<
        {
          createDiscipline: DisciplineOutput;
        },
        MutationCreateDisciplineArgs
      >(CREATE_DISCIPLINE, { input });
      return data.createDiscipline;
    },
    onSuccess: () =>
      queryClient.invalidateQueries({
        queryKey: ['disciplines'],
      }),
  });
};

export const useUpdateDiscipline = () => {
  const queryClient = useQueryClient();
  return useMutation<
    boolean,
    Error,
    { id: string; input: UpdateDisciplineInput }
  >({
    mutationFn: async ({ id, input }) => {
      const data = await graphqlFetch<
        { updateDiscipline: boolean },
        MutationUpdateDisciplineArgs
      >(UPDATE_DISCIPLINE, { id, input });
      return data.updateDiscipline;
    },
    onSuccess: () =>
      queryClient.invalidateQueries({
        queryKey: ['disciplines'],
      }),
  });
};

export const useRemoveDiscipline = () => {
  const queryClient = useQueryClient();
  return useMutation<boolean, Error, string>({
    mutationFn: async (id) => {
      const data = await graphqlFetch<
        { removeDiscipline: boolean },
        MutationRemoveDisciplineArgs
      >(REMOVE_DISCIPLINE, { id });
      return data.removeDiscipline;
    },
    onSuccess: () =>
      queryClient.invalidateQueries({
        queryKey: ['disciplines'],
      }),
  });
};
